import { useState, useRef, useEffect } from 'react';
import Sidebar from '../../components/feature/Sidebar';
import { useFinanceRecords } from '../../store/financeStore';
import type { FinanceRecord } from '../../store/financeStore';
import ProfitAnalysisChart from './components/ProfitAnalysisChart';

type TabKey = 'overview' | 'detail' | 'tax' | 'subsidy';

function StatCard({ icon, label, value, sub, color, bg }: { icon: string; label: string; value: string; sub?: string; color: string; bg: string }) {
  return (
    <div className={`${bg} rounded-xl px-5 py-4 flex items-center gap-4`}>
      <div className={`w-11 h-11 flex items-center justify-center rounded-xl bg-white shadow-sm ${color}`}>
        <i className={`${icon} text-xl`}></i>
      </div>
      <div>
        <div className="text-xs text-gray-400 font-medium">{label}</div>
        <div className={`text-xl font-bold ${color}`}>{value}</div>
        {sub && <div className="text-xs text-gray-400 mt-0.5">{sub}</div>}
      </div>
    </div>
  );
}

function DetailModal({ record, onClose, onSave }: { record: FinanceRecord; onClose: () => void; onSave: (r: FinanceRecord) => void }) {
  const [remark, setRemark] = useState(record.remark);
  const [saved, setSaved] = useState(false);

  const handleSave = () => {
    onSave({ ...record, remark });
    setSaved(true);
    setTimeout(() => setSaved(false), 2000);
  };

  const rows = [
    { label: '业务日期', value: record.date },
    { label: '业务类型', value: record.bizType },
    { label: '粮食品种', value: record.variety },
    { label: '数量', value: `${record.quantity.toLocaleString()} ${record.unit}` },
    { label: '单价（元/吨）', value: `¥${record.unitPrice.toLocaleString()}` },
    { label: '交易金额', value: `¥${record.amount.toLocaleString()}` },
    { label: '结算方式', value: record.settlement },
    { label: '交易对象', value: record.counterpart },
  ];

  const finRows = record.bizType === '销售'
    ? [
        { label: '营业收入', value: `¥${record.revenue.toLocaleString()}` },
        { label: '营业成本', value: `¥${record.cost.toLocaleString()}` },
        { label: '毛利润', value: `¥${record.grossProfit.toLocaleString()}`, highlight: true },
        { label: '税款（增值税）', value: `¥${record.tax.toLocaleString()}（税率 ${record.taxRate}%）` },
      ]
    : [
        { label: '采购成本', value: `¥${record.cost.toLocaleString()}` },
        { label: '政府补贴', value: record.subsidy > 0 ? `¥${record.subsidy.toLocaleString()}` : '无' },
        { label: '补贴类型', value: record.subsidyType || '—' },
      ];

  return (
    <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4" onClick={onClose}>
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-2xl max-h-[90vh] overflow-y-auto" onClick={e => e.stopPropagation()}>
        <div className="flex items-center justify-between px-6 py-5 border-b border-gray-100 sticky top-0 bg-white z-10">
          <div className="flex items-center gap-3">
            <div className="w-9 h-9 flex items-center justify-center rounded-xl bg-teal-50 text-teal-600">
              <i className="ri-bill-line text-lg"></i>
            </div>
            <div>
              <h3 className="text-base font-bold text-gray-900">财务记录详情</h3>
              <p className="text-xs text-gray-400">{record.date} · {record.bizType} · {record.variety}</p>
            </div>
          </div>
          <button onClick={onClose} className="w-8 h-8 flex items-center justify-center rounded-lg hover:bg-gray-100 text-gray-400 cursor-pointer">
            <i className="ri-close-line text-lg"></i>
          </button>
        </div>

        <div className="p-6 space-y-5">
          {/* 基础信息 */}
          <div>
            <div className="flex items-center gap-2 mb-3">
              <span className="w-1 h-4 bg-teal-500 rounded-full"></span>
              <span className="text-sm font-semibold text-gray-700">基础信息</span>
            </div>
            <div className="grid grid-cols-2 gap-0 border border-gray-100 rounded-xl overflow-hidden">
              {rows.map((row, i) => (
                <div key={i} className={`flex items-center justify-between px-4 py-3 ${i % 2 === 0 ? 'bg-gray-50/60' : 'bg-white'} border-b border-gray-100 last:border-0`}>
                  <span className="text-xs text-gray-400">{row.label}</span>
                  <span className="text-sm font-medium text-gray-800">{row.value}</span>
                </div>
              ))}
            </div>
          </div>

          {/* 财务信息 */}
          <div>
            <div className="flex items-center gap-2 mb-3">
              <span className="w-1 h-4 bg-amber-500 rounded-full"></span>
              <span className="text-sm font-semibold text-gray-700">财务信息</span>
            </div>
            <div className="border border-gray-100 rounded-xl overflow-hidden">
              {finRows.map((row, i) => (
                <div key={i} className={`flex items-center justify-between px-4 py-3 border-b border-gray-100 last:border-0 ${row.highlight ? 'bg-teal-50' : i % 2 === 0 ? 'bg-gray-50/60' : 'bg-white'}`}>
                  <span className="text-xs text-gray-400">{row.label}</span>
                  <span className={`text-sm font-semibold ${row.highlight ? 'text-teal-600' : 'text-gray-800'}`}>{row.value}</span>
                </div>
              ))}
            </div>
          </div>

          {/* 备注（可编辑） */}
          <div>
            <div className="flex items-center gap-2 mb-3">
              <span className="w-1 h-4 bg-gray-300 rounded-full"></span>
              <span className="text-sm font-semibold text-gray-700">备注信息</span>
              <span className="text-xs text-gray-400 ml-1">（仅此项可修改）</span>
            </div>
            <textarea
              value={remark}
              onChange={e => setRemark(e.target.value.slice(0, 500))}
              maxLength={500}
              rows={3}
              placeholder="请输入备注信息..."
              className="w-full px-4 py-3 border border-gray-200 rounded-xl text-sm text-gray-700 focus:ring-2 focus:ring-teal-400 focus:border-transparent resize-none"
            />
            <div className="text-right text-xs text-gray-300 mt-1">{remark.length}/500</div>
          </div>

          <div className="flex items-center justify-between pt-2">
            {saved && (
              <span className="flex items-center gap-1.5 text-teal-600 text-sm font-medium">
                <i className="ri-check-circle-fill"></i> 保存成功
              </span>
            )}
            {!saved && <span></span>}
            <div className="flex gap-3">
              <button onClick={onClose} className="px-5 py-2 border border-gray-200 text-gray-600 rounded-lg text-sm hover:bg-gray-50 transition-colors cursor-pointer whitespace-nowrap">取消</button>
              <button onClick={handleSave} className="px-5 py-2 bg-teal-600 text-white rounded-lg text-sm hover:bg-teal-700 transition-colors cursor-pointer whitespace-nowrap">保存备注</button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

const PAGE_SIZE = 10;

export default function Finance() {
  const { records, save: handleSaveRecord } = useFinanceRecords();
  const [activeTab, setActiveTab] = useState<TabKey>('overview');
  const [filterType, setFilterType] = useState('');
  const [filterVariety, setFilterVariety] = useState('');
  const [filterSettlement, setFilterSettlement] = useState('');
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [keyword, setKeyword] = useState('');
  const [page, setPage] = useState(1);
  const [detailRecord, setDetailRecord] = useState<FinanceRecord | null>(null);
  const [showExport, setShowExport] = useState(false);
  const exportRef = useRef<HTMLDivElement>(null);

  // Close export dropdown when clicking outside
  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (exportRef.current && !exportRef.current.contains(e.target as Node)) {
        setShowExport(false);
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  const filtered = records.filter(r => {
    if (filterType && r.bizType !== filterType) return false;
    if (filterVariety && r.variety !== filterVariety) return false;
    if (filterSettlement && r.settlement !== filterSettlement) return false;
    if (startDate && r.date < startDate) return false;
    if (endDate && r.date > endDate) return false;
    if (keyword && !r.counterpart.includes(keyword) && !r.variety.includes(keyword)) return false;
    return true;
  });

  const totalPages = Math.max(1, Math.ceil(filtered.length / PAGE_SIZE));
  const paginated = filtered.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE);

  // 统计
  const salesRecords = records.filter(r => r.bizType === '销售');
  const purchaseRecords = records.filter(r => r.bizType === '收购');
  const totalRevenue = salesRecords.reduce((s, r) => s + r.revenue, 0);
  const totalCost = records.reduce((s, r) => s + r.cost, 0);
  const totalGrossProfit = salesRecords.reduce((s, r) => s + r.grossProfit, 0);
  const totalTax = salesRecords.reduce((s, r) => s + r.tax, 0);
  const totalSubsidy = purchaseRecords.reduce((s, r) => s + r.subsidy, 0);
  const totalPurchaseAmount = purchaseRecords.reduce((s, r) => s + r.amount, 0);
  const totalSalesAmount = salesRecords.reduce((s, r) => s + r.amount, 0);

  // 按品种汇总
  const varietySummary = Array.from(new Set(records.map(r => r.variety))).map(v => {
    const vSales = records.filter(r => r.variety === v && r.bizType === '销售');
    const vPurchase = records.filter(r => r.variety === v && r.bizType === '收购');
    return {
      variety: v,
      purchaseQty: vPurchase.reduce((s, r) => s + r.quantity, 0),
      purchaseAmt: vPurchase.reduce((s, r) => s + r.amount, 0),
      salesQty: vSales.reduce((s, r) => s + r.quantity, 0),
      salesAmt: vSales.reduce((s, r) => s + r.amount, 0),
      grossProfit: vSales.reduce((s, r) => s + r.grossProfit, 0),
      tax: vSales.reduce((s, r) => s + r.tax, 0),
      subsidy: vPurchase.reduce((s, r) => s + r.subsidy, 0),
    };
  });

  // 结算方式分布
  const settlementMap: Record<string, number> = {};
  records.forEach(r => {
    settlementMap[r.settlement] = (settlementMap[r.settlement] || 0) + r.amount;
  });
  const settlementTotal = Object.values(settlementMap).reduce((a, b) => a + b, 0);

  const tabs: { key: TabKey; label: string; icon: string }[] = [
    { key: 'overview', label: '财务概览', icon: 'ri-pie-chart-2-line' },
    { key: 'detail', label: '收支明细', icon: 'ri-file-list-3-line' },
    { key: 'tax', label: '税款管理', icon: 'ri-government-line' },
    { key: 'subsidy', label: '政府补贴', icon: 'ri-hand-coin-line' },
  ];

  return (
    <div className="flex min-h-screen bg-slate-50">
      <Sidebar />

      {detailRecord && (
        <DetailModal
          record={detailRecord}
          onClose={() => setDetailRecord(null)}
          onSave={handleSaveRecord}
        />
      )}

      <div className="flex-1 ml-64">
        {/* 顶部 */}
        <div className="bg-white border-b border-gray-100 px-8 py-5 flex items-center justify-between sticky top-0 z-10">
          <div>
            <h1 className="text-xl font-bold text-gray-900">财务管理</h1>
            <p className="text-xs text-gray-400 mt-0.5">粮食经营资金财务信息管理</p>
          </div>
          <div className="flex items-center gap-3">
            <div ref={exportRef} className="relative">
              <button
                onClick={() => setShowExport(v => !v)}
                className="flex items-center gap-2 px-4 py-2.5 border border-gray-200 text-gray-600 rounded-lg hover:bg-gray-50 transition-colors text-sm font-medium whitespace-nowrap cursor-pointer"
              >
                <i className="ri-download-2-line"></i>
                导出报表
                <i className={`ri-arrow-down-s-line transition-transform ${showExport ? 'rotate-180' : ''}`}></i>
              </button>
              {showExport && (
                <div className="absolute right-0 top-full mt-2 w-44 bg-white border border-gray-100 rounded-xl shadow-lg z-30 overflow-hidden">
                  {[
                    { key: 'excel', label: '导出 Excel', icon: 'ri-file-excel-2-line', color: 'text-emerald-600' },
                    { key: 'csv', label: '导出 CSV', icon: 'ri-file-text-line', color: 'text-teal-600' },
                    { key: 'pdf', label: '导出 PDF', icon: 'ri-file-pdf-2-line', color: 'text-rose-600' },
                  ].map(f => (
                    <button
                      key={f.key}
                      onClick={() => setShowExport(false)}
                      className="w-full flex items-center gap-3 px-4 py-3 text-sm text-gray-700 hover:bg-gray-50 transition-colors cursor-pointer"
                    >
                      <i className={`${f.icon} text-base ${f.color}`}></i>
                      {f.label}
                    </button>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>

        <div className="p-8 space-y-6">
          {/* 统计卡片 */}
          <div className="grid grid-cols-5 gap-4">
            <StatCard icon="ri-money-cny-circle-line" label="营业收入" value={`¥${(totalRevenue / 10000).toFixed(1)}万`} sub="本期销售收入合计" color="text-teal-600" bg="bg-teal-50" />
            <StatCard icon="ri-shopping-cart-2-line" label="采购成本" value={`¥${(totalPurchaseAmount / 10000).toFixed(1)}万`} sub="本期收购支出合计" color="text-amber-600" bg="bg-amber-50" />
            <StatCard icon="ri-line-chart-line" label="毛利润" value={`¥${(totalGrossProfit / 10000).toFixed(1)}万`} sub={`利润率 ${totalRevenue > 0 ? ((totalGrossProfit / totalRevenue) * 100).toFixed(1) : 0}%`} color="text-emerald-600" bg="bg-emerald-50" />
            <StatCard icon="ri-government-line" label="应缴税款" value={`¥${(totalTax / 10000).toFixed(1)}万`} sub="增值税合计" color="text-rose-500" bg="bg-rose-50" />
            <StatCard icon="ri-hand-coin-line" label="政府补贴" value={`¥${(totalSubsidy / 10000).toFixed(2)}万`} sub="收购补贴合计" color="text-indigo-500" bg="bg-indigo-50" />
          </div>

          {/* 利润分析图表 */}
          <ProfitAnalysisChart records={records} />

          {/* Tab 切换 */}
          <div className="bg-white rounded-xl border border-gray-100 shadow-sm overflow-hidden">
            <div className="flex border-b border-gray-100 px-6 pt-4">
              {tabs.map(tab => (
                <button
                  key={tab.key}
                  onClick={() => setActiveTab(tab.key)}
                  className={`flex items-center gap-2 px-5 py-3 text-sm font-medium border-b-2 transition-all cursor-pointer whitespace-nowrap mr-1 ${
                    activeTab === tab.key
                      ? 'border-teal-500 text-teal-600'
                      : 'border-transparent text-gray-400 hover:text-gray-600'
                  }`}
                >
                  <i className={`${tab.icon} text-base`}></i>
                  {tab.label}
                </button>
              ))}
            </div>

            {/* 财务概览 */}
            {activeTab === 'overview' && (
              <div className="p-6 space-y-6">
                {/* 收支对比 */}
                <div className="grid grid-cols-2 gap-6">
                  <div>
                    <h4 className="text-sm font-semibold text-gray-700 mb-4 flex items-center gap-2">
                      <i className="ri-bar-chart-grouped-line text-teal-500"></i>
                      收支金额对比
                    </h4>
                    <div className="space-y-4">
                      {[
                        { label: '销售收入', value: totalSalesAmount, color: 'bg-teal-500', max: Math.max(totalSalesAmount, totalPurchaseAmount) },
                        { label: '采购支出', value: totalPurchaseAmount, color: 'bg-amber-400', max: Math.max(totalSalesAmount, totalPurchaseAmount) },
                        { label: '毛利润', value: totalGrossProfit, color: 'bg-emerald-500', max: Math.max(totalSalesAmount, totalPurchaseAmount) },
                        { label: '应缴税款', value: totalTax, color: 'bg-rose-400', max: Math.max(totalSalesAmount, totalPurchaseAmount) },
                        { label: '政府补贴', value: totalSubsidy, color: 'bg-indigo-400', max: Math.max(totalSalesAmount, totalPurchaseAmount) },
                      ].map(item => (
                        <div key={item.label}>
                          <div className="flex items-center justify-between mb-1.5">
                            <span className="text-xs text-gray-500">{item.label}</span>
                            <span className="text-xs font-semibold text-gray-700">¥{(item.value / 10000).toFixed(2)}万</span>
                          </div>
                          <div className="h-2.5 bg-gray-100 rounded-full overflow-hidden">
                            <div
                              className={`h-full ${item.color} rounded-full transition-all duration-700`}
                              style={{ width: `${item.max > 0 ? (item.value / item.max) * 100 : 0}%` }}
                            ></div>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>

                  <div>
                    <h4 className="text-sm font-semibold text-gray-700 mb-4 flex items-center gap-2">
                      <i className="ri-exchange-cny-line text-amber-500"></i>
                      结算方式分布
                    </h4>
                    <div className="space-y-3">
                      {Object.entries(settlementMap).map(([method, amt]) => {
                        const pct = settlementTotal > 0 ? ((amt / settlementTotal) * 100).toFixed(1) : '0';
                        const colors: Record<string, string> = { '转账': 'bg-teal-500', '现金': 'bg-amber-400', '承兑': 'bg-rose-400', '赊账': 'bg-gray-400' };
                        return (
                          <div key={method}>
                            <div className="flex items-center justify-between mb-1.5">
                              <div className="flex items-center gap-2">
                                <span className={`w-2.5 h-2.5 rounded-full ${colors[method] || 'bg-gray-400'}`}></span>
                                <span className="text-xs text-gray-500">{method}</span>
                              </div>
                              <div className="flex items-center gap-3">
                                <span className="text-xs text-gray-400">¥{(amt / 10000).toFixed(1)}万</span>
                                <span className="text-xs font-semibold text-gray-700 w-10 text-right">{pct}%</span>
                              </div>
                            </div>
                            <div className="h-2 bg-gray-100 rounded-full overflow-hidden">
                              <div className={`h-full ${colors[method] || 'bg-gray-400'} rounded-full`} style={{ width: `${pct}%` }}></div>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                </div>

                {/* 分品种汇总 */}
                <div>
                  <h4 className="text-sm font-semibold text-gray-700 mb-4 flex items-center gap-2">
                    <i className="ri-plant-line text-teal-500"></i>
                    分品种财务汇总
                  </h4>
                  <div className="overflow-x-auto rounded-xl border border-gray-100">
                    <table className="w-full">
                      <thead>
                        <tr className="bg-gray-50">
                          <th className="text-left py-3 px-4 text-xs font-semibold text-gray-400 uppercase tracking-wide whitespace-nowrap">品种</th>
                          <th className="text-right py-3 px-4 text-xs font-semibold text-gray-400 uppercase tracking-wide whitespace-nowrap">收购量(吨)</th>
                          <th className="text-right py-3 px-4 text-xs font-semibold text-gray-400 uppercase tracking-wide whitespace-nowrap">收购金额</th>
                          <th className="text-right py-3 px-4 text-xs font-semibold text-gray-400 uppercase tracking-wide whitespace-nowrap">销售量(吨)</th>
                          <th className="text-right py-3 px-4 text-xs font-semibold text-gray-400 uppercase tracking-wide whitespace-nowrap">销售收入</th>
                          <th className="text-right py-3 px-4 text-xs font-semibold text-gray-400 uppercase tracking-wide whitespace-nowrap">毛利润</th>
                          <th className="text-right py-3 px-4 text-xs font-semibold text-gray-400 uppercase tracking-wide whitespace-nowrap">税款</th>
                          <th className="text-right py-3 px-4 text-xs font-semibold text-gray-400 uppercase tracking-wide whitespace-nowrap">补贴</th>
                        </tr>
                      </thead>
                      <tbody>
                        {varietySummary.map((v, i) => (
                          <tr key={v.variety} className={`border-t border-gray-50 hover:bg-teal-50/20 transition-colors ${i % 2 === 1 ? 'bg-gray-50/40' : ''}`}>
                            <td className="py-3 px-4 text-sm font-semibold text-gray-800 whitespace-nowrap">{v.variety}</td>
                            <td className="py-3 px-4 text-sm text-right text-gray-600 whitespace-nowrap">{v.purchaseQty.toLocaleString()}</td>
                            <td className="py-3 px-4 text-sm text-right text-amber-600 font-medium whitespace-nowrap">¥{(v.purchaseAmt / 10000).toFixed(1)}万</td>
                            <td className="py-3 px-4 text-sm text-right text-gray-600 whitespace-nowrap">{v.salesQty.toLocaleString()}</td>
                            <td className="py-3 px-4 text-sm text-right text-teal-600 font-medium whitespace-nowrap">¥{(v.salesAmt / 10000).toFixed(1)}万</td>
                            <td className="py-3 px-4 text-sm text-right text-emerald-600 font-semibold whitespace-nowrap">¥{v.grossProfit.toLocaleString()}</td>
                            <td className="py-3 px-4 text-sm text-right text-rose-500 whitespace-nowrap">¥{v.tax.toLocaleString()}</td>
                            <td className="py-3 px-4 text-sm text-right text-indigo-500 whitespace-nowrap">{v.subsidy > 0 ? `¥${v.subsidy.toLocaleString()}` : '—'}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              </div>
            )}

            {/* 收支明细 */}
            {activeTab === 'detail' && (
              <div className="p-6 space-y-4">
                {/* 筛选 */}
                <div className="grid grid-cols-6 gap-3">
                  <input type="date" value={startDate} onChange={e => { setStartDate(e.target.value); setPage(1); }} className="px-3 py-2 border border-gray-200 rounded-lg text-sm focus:ring-2 focus:ring-teal-400 focus:border-transparent" />
                  <input type="date" value={endDate} onChange={e => { setEndDate(e.target.value); setPage(1); }} className="px-3 py-2 border border-gray-200 rounded-lg text-sm focus:ring-2 focus:ring-teal-400 focus:border-transparent" />
                  <select value={filterType} onChange={e => { setFilterType(e.target.value); setPage(1); }} className="px-3 py-2 border border-gray-200 rounded-lg text-sm focus:ring-2 focus:ring-teal-400 focus:border-transparent cursor-pointer">
                    <option value="">全部类型</option>
                    <option value="收购">收购</option>
                    <option value="销售">销售</option>
                  </select>
                  <select value={filterVariety} onChange={e => { setFilterVariety(e.target.value); setPage(1); }} className="px-3 py-2 border border-gray-200 rounded-lg text-sm focus:ring-2 focus:ring-teal-400 focus:border-transparent cursor-pointer">
                    <option value="">全部品种</option>
                    {['小麦', '玉米', '大米', '大豆', '高粱'].map(v => <option key={v} value={v}>{v}</option>)}
                  </select>
                  <select value={filterSettlement} onChange={e => { setFilterSettlement(e.target.value); setPage(1); }} className="px-3 py-2 border border-gray-200 rounded-lg text-sm focus:ring-2 focus:ring-teal-400 focus:border-transparent cursor-pointer">
                    <option value="">全部结算</option>
                    {['现金', '转账', '承兑', '赊账'].map(s => <option key={s} value={s}>{s}</option>)}
                  </select>
                  <div className="relative">
                    <i className="ri-search-line absolute left-3 top-1/2 -translate-y-1/2 text-gray-300 text-sm"></i>
                    <input type="text" value={keyword} onChange={e => { setKeyword(e.target.value); setPage(1); }} placeholder="搜索交易对象" className="w-full pl-8 pr-3 py-2 border border-gray-200 rounded-lg text-sm focus:ring-2 focus:ring-teal-400 focus:border-transparent" />
                  </div>
                </div>

                {/* 表格 */}
                <div className="overflow-x-auto rounded-xl border border-gray-100">
                  <table className="w-full">
                    <thead>
                      <tr className="bg-gray-50">
                        {['日期', '类型', '品种', '数量', '单价(元/吨)', '交易金额', '营业收入', '营业成本', '毛利润', '结算方式', '交易对象', '操作'].map(h => (
                          <th key={h} className="text-left py-3 px-4 text-xs font-semibold text-gray-400 uppercase tracking-wide whitespace-nowrap">{h}</th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {paginated.length === 0 ? (
                        <tr>
                          <td colSpan={12} className="py-16 text-center text-gray-300">
                            <i className="ri-file-search-line text-4xl block mb-2"></i>
                            <span className="text-sm">暂无符合条件的记录</span>
                          </td>
                        </tr>
                      ) : paginated.map((r, i) => (
                        <tr key={r.id} className={`border-t border-gray-50 hover:bg-teal-50/20 transition-colors ${i % 2 === 1 ? 'bg-gray-50/40' : ''}`}>
                          <td className="py-3 px-4 text-sm text-gray-500 whitespace-nowrap">{r.date}</td>
                          <td className="py-3 px-4">
                            <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-semibold whitespace-nowrap ${r.bizType === '收购' ? 'bg-teal-50 text-teal-600' : 'bg-orange-50 text-orange-500'}`}>
                              <i className={`text-xs ${r.bizType === '收购' ? 'ri-arrow-down-line' : 'ri-arrow-up-line'}`}></i>
                              {r.bizType}
                            </span>
                          </td>
                          <td className="py-3 px-4 text-sm font-semibold text-gray-800 whitespace-nowrap">{r.variety}</td>
                          <td className="py-3 px-4 text-sm text-gray-600 whitespace-nowrap">{r.quantity.toLocaleString()} {r.unit}</td>
                          <td className="py-3 px-4 text-sm text-gray-600 whitespace-nowrap">¥{r.unitPrice.toLocaleString()}</td>
                          <td className="py-3 px-4 text-sm font-bold text-gray-800 whitespace-nowrap">¥{r.amount.toLocaleString()}</td>
                          <td className="py-3 px-4 text-sm text-teal-600 font-medium whitespace-nowrap">{r.revenue > 0 ? `¥${r.revenue.toLocaleString()}` : '—'}</td>
                          <td className="py-3 px-4 text-sm text-amber-600 font-medium whitespace-nowrap">¥{r.cost.toLocaleString()}</td>
                          <td className="py-3 px-4 text-sm whitespace-nowrap">
                            {r.grossProfit > 0 ? (
                              <span className="text-emerald-600 font-semibold">+¥{r.grossProfit.toLocaleString()}</span>
                            ) : <span className="text-gray-300">—</span>}
                          </td>
                          <td className="py-3 px-4">
                            <span className="px-2 py-0.5 bg-gray-100 text-gray-500 text-xs rounded-md whitespace-nowrap">{r.settlement}</span>
                          </td>
                          <td className="py-3 px-4 text-sm text-gray-700 whitespace-nowrap">{r.counterpart}</td>
                          <td className="py-3 px-4">
                            <button onClick={() => setDetailRecord(r)} className="w-7 h-7 flex items-center justify-center text-teal-500 hover:bg-teal-50 rounded-lg transition-colors cursor-pointer">
                              <i className="ri-eye-line text-sm"></i>
                            </button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>

                {/* 分页 */}
                {filtered.length > 0 && (
                  <div className="flex items-center justify-between pt-2">
                    <span className="text-xs text-gray-400">共 {filtered.length} 条记录</span>
                    <div className="flex items-center gap-1.5">
                      <button onClick={() => setPage(p => Math.max(1, p - 1))} disabled={page === 1} className="w-8 h-8 flex items-center justify-center border border-gray-200 text-gray-500 rounded-lg hover:bg-gray-50 disabled:opacity-30 disabled:cursor-not-allowed transition-colors">
                        <i className="ri-arrow-left-s-line text-sm"></i>
                      </button>
                      {Array.from({ length: totalPages }, (_, i) => i + 1).map(p => (
                        <button key={p} onClick={() => setPage(p)} className={`w-8 h-8 flex items-center justify-center rounded-lg text-sm font-medium transition-colors whitespace-nowrap ${page === p ? 'bg-teal-600 text-white' : 'border border-gray-200 text-gray-600 hover:bg-gray-50'}`}>{p}</button>
                      ))}
                      <button onClick={() => setPage(p => Math.min(totalPages, p + 1))} disabled={page === totalPages} className="w-8 h-8 flex items-center justify-center border border-gray-200 text-gray-500 rounded-lg hover:bg-gray-50 disabled:opacity-30 disabled:cursor-not-allowed transition-colors">
                        <i className="ri-arrow-right-s-line text-sm"></i>
                      </button>
                    </div>
                  </div>
                )}
              </div>
            )}

            {/* 税款管理 */}
            {activeTab === 'tax' && (
              <div className="p-6 space-y-5">
                <div className="grid grid-cols-3 gap-4">
                  <div className="bg-rose-50 rounded-xl p-5">
                    <div className="flex items-center gap-3 mb-3">
                      <div className="w-10 h-10 flex items-center justify-center rounded-xl bg-white shadow-sm text-rose-500">
                        <i className="ri-government-line text-xl"></i>
                      </div>
                      <span className="text-sm font-semibold text-gray-700">应缴税款合计</span>
                    </div>
                    <div className="text-2xl font-bold text-rose-500">¥{totalTax.toLocaleString()}</div>
                    <div className="text-xs text-gray-400 mt-1">增值税（税率 3%）</div>
                  </div>
                  <div className="bg-amber-50 rounded-xl p-5">
                    <div className="flex items-center gap-3 mb-3">
                      <div className="w-10 h-10 flex items-center justify-center rounded-xl bg-white shadow-sm text-amber-500">
                        <i className="ri-percent-line text-xl"></i>
                      </div>
                      <span className="text-sm font-semibold text-gray-700">税负率</span>
                    </div>
                    <div className="text-2xl font-bold text-amber-500">{totalRevenue > 0 ? ((totalTax / totalRevenue) * 100).toFixed(2) : 0}%</div>
                    <div className="text-xs text-gray-400 mt-1">税款 / 营业收入</div>
                  </div>
                  <div className="bg-teal-50 rounded-xl p-5">
                    <div className="flex items-center gap-3 mb-3">
                      <div className="w-10 h-10 flex items-center justify-center rounded-xl bg-white shadow-sm text-teal-500">
                        <i className="ri-money-cny-box-line text-xl"></i>
                      </div>
                      <span className="text-sm font-semibold text-gray-700">税后净利润</span>
                    </div>
                    <div className="text-2xl font-bold text-teal-600">¥{(totalGrossProfit - totalTax).toLocaleString()}</div>
                    <div className="text-xs text-gray-400 mt-1">毛利润 - 税款</div>
                  </div>
                </div>

                <div className="overflow-x-auto rounded-xl border border-gray-100">
                  <table className="w-full">
                    <thead>
                      <tr className="bg-gray-50">
                        {['日期', '品种', '销售数量', '销售单价', '销售收入', '税率', '应缴税款', '交易对象'].map(h => (
                          <th key={h} className="text-left py-3 px-4 text-xs font-semibold text-gray-400 uppercase tracking-wide whitespace-nowrap">{h}</th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {salesRecords.map((r, i) => (
                        <tr key={r.id} className={`border-t border-gray-50 hover:bg-rose-50/20 transition-colors ${i % 2 === 1 ? 'bg-gray-50/40' : ''}`}>
                          <td className="py-3 px-4 text-sm text-gray-500 whitespace-nowrap">{r.date}</td>
                          <td className="py-3 px-4 text-sm font-semibold text-gray-800 whitespace-nowrap">{r.variety}</td>
                          <td className="py-3 px-4 text-sm text-gray-600 whitespace-nowrap">{r.quantity.toLocaleString()} {r.unit}</td>
                          <td className="py-3 px-4 text-sm text-gray-600 whitespace-nowrap">¥{r.unitPrice.toLocaleString()}</td>
                          <td className="py-3 px-4 text-sm text-teal-600 font-medium whitespace-nowrap">¥{r.revenue.toLocaleString()}</td>
                          <td className="py-3 px-4 text-sm text-gray-600 whitespace-nowrap">{r.taxRate}%</td>
                          <td className="py-3 px-4 text-sm text-rose-500 font-bold whitespace-nowrap">¥{r.tax.toLocaleString()}</td>
                          <td className="py-3 px-4 text-sm text-gray-700 whitespace-nowrap">{r.counterpart}</td>
                        </tr>
                      ))}
                      <tr className="bg-rose-50/50 border-t-2 border-rose-100 font-semibold">
                        <td colSpan={4} className="py-3 px-4 text-sm text-gray-700">合计</td>
                        <td className="py-3 px-4 text-sm text-teal-600 whitespace-nowrap">¥{totalRevenue.toLocaleString()}</td>
                        <td className="py-3 px-4 text-sm text-gray-500">—</td>
                        <td className="py-3 px-4 text-sm text-rose-500 whitespace-nowrap">¥{totalTax.toLocaleString()}</td>
                        <td></td>
                      </tr>
                    </tbody>
                  </table>
                </div>
              </div>
            )}

            {/* 政府补贴 */}
            {activeTab === 'subsidy' && (
              <div className="p-6 space-y-5">
                <div className="grid grid-cols-3 gap-4">
                  <div className="bg-indigo-50 rounded-xl p-5">
                    <div className="flex items-center gap-3 mb-3">
                      <div className="w-10 h-10 flex items-center justify-center rounded-xl bg-white shadow-sm text-indigo-500">
                        <i className="ri-hand-coin-line text-xl"></i>
                      </div>
                      <span className="text-sm font-semibold text-gray-700">补贴总金额</span>
                    </div>
                    <div className="text-2xl font-bold text-indigo-500">¥{totalSubsidy.toLocaleString()}</div>
                    <div className="text-xs text-gray-400 mt-1">本期政府补贴合计</div>
                  </div>
                  <div className="bg-teal-50 rounded-xl p-5">
                    <div className="flex items-center gap-3 mb-3">
                      <div className="w-10 h-10 flex items-center justify-center rounded-xl bg-white shadow-sm text-teal-500">
                        <i className="ri-file-list-check-line text-xl"></i>
                      </div>
                      <span className="text-sm font-semibold text-gray-700">补贴笔数</span>
                    </div>
                    <div className="text-2xl font-bold text-teal-600">{purchaseRecords.filter(r => r.subsidy > 0).length} 笔</div>
                    <div className="text-xs text-gray-400 mt-1">本期获得补贴次数</div>
                  </div>
                  <div className="bg-amber-50 rounded-xl p-5">
                    <div className="flex items-center gap-3 mb-3">
                      <div className="w-10 h-10 flex items-center justify-center rounded-xl bg-white shadow-sm text-amber-500">
                        <i className="ri-calculator-line text-xl"></i>
                      </div>
                      <span className="text-sm font-semibold text-gray-700">平均补贴/笔</span>
                    </div>
                    <div className="text-2xl font-bold text-amber-500">
                      ¥{purchaseRecords.filter(r => r.subsidy > 0).length > 0
                        ? Math.round(totalSubsidy / purchaseRecords.filter(r => r.subsidy > 0).length).toLocaleString()
                        : 0}
                    </div>
                    <div className="text-xs text-gray-400 mt-1">补贴总额 / 补贴笔数</div>
                  </div>
                </div>

                <div className="overflow-x-auto rounded-xl border border-gray-100">
                  <table className="w-full">
                    <thead>
                      <tr className="bg-gray-50">
                        {['日期', '品种', '收购数量', '收购单价', '收购金额', '补贴类型', '补贴金额', '供应商'].map(h => (
                          <th key={h} className="text-left py-3 px-4 text-xs font-semibold text-gray-400 uppercase tracking-wide whitespace-nowrap">{h}</th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {purchaseRecords.filter(r => r.subsidy > 0).map((r, i) => (
                        <tr key={r.id} className={`border-t border-gray-50 hover:bg-indigo-50/20 transition-colors ${i % 2 === 1 ? 'bg-gray-50/40' : ''}`}>
                          <td className="py-3 px-4 text-sm text-gray-500 whitespace-nowrap">{r.date}</td>
                          <td className="py-3 px-4 text-sm font-semibold text-gray-800 whitespace-nowrap">{r.variety}</td>
                          <td className="py-3 px-4 text-sm text-gray-600 whitespace-nowrap">{r.quantity.toLocaleString()} {r.unit}</td>
                          <td className="py-3 px-4 text-sm text-gray-600 whitespace-nowrap">¥{r.unitPrice.toLocaleString()}</td>
                          <td className="py-3 px-4 text-sm text-amber-600 font-medium whitespace-nowrap">¥{r.amount.toLocaleString()}</td>
                          <td className="py-3 px-4">
                            <span className="px-2 py-0.5 bg-indigo-50 text-indigo-600 text-xs rounded-full whitespace-nowrap">{r.subsidyType}</span>
                          </td>
                          <td className="py-3 px-4 text-sm text-indigo-600 font-bold whitespace-nowrap">¥{r.subsidy.toLocaleString()}</td>
                          <td className="py-3 px-4 text-sm text-gray-700 whitespace-nowrap">{r.counterpart}</td>
                        </tr>
                      ))}
                      <tr className="bg-indigo-50/50 border-t-2 border-indigo-100 font-semibold">
                        <td colSpan={5} className="py-3 px-4 text-sm text-gray-700">合计</td>
                        <td className="py-3 px-4 text-sm text-gray-500">—</td>
                        <td className="py-3 px-4 text-sm text-indigo-600 whitespace-nowrap">¥{totalSubsidy.toLocaleString()}</td>
                        <td></td>
                      </tr>
                    </tbody>
                  </table>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
